import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Printer, QrCode, Trash2, Search, RefreshCw } from "lucide-react";
import { showError } from "../lib/errorHandler";
import { openBoxLabels } from "../lib/boxLabel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIFT_LABEL = { morning: "Manhã", afternoon: "Tarde", night: "Noite" };

export default function BoxLabels() {
  const [orders, setOrders] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  const fetchOrders = async () => {
    try {
      const r = await axios.get(`${API}/orders`);
      setOrders(r.data || []);
    } catch (e) { showError(e, "Erro a carregar ordens"); }
  };

  const fetchLabels = async () => {
    setLoading(true);
    try {
      if (selectedOrder === "all") {
        // Buscar todas as ordens e juntar
        const r = await axios.get(`${API}/orders`);
        const allOrders = r.data || [];
        const results = await Promise.all(
          allOrders.map(o => axios.get(`${API}/orders/${o.id}/box-labels`).then(r => r.data).catch(() => []))
        );
        const flat = results.flat();
        flat.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setLabels(flat);
      } else {
        const r = await axios.get(`${API}/orders/${selectedOrder}/box-labels`);
        setLabels(r.data || []);
      }
    } catch (e) {
      showError(e, "Erro a carregar etiquetas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);
  useEffect(() => { fetchLabels(); }, [selectedOrder]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return labels;
    return labels.filter(l =>
      (l.lot_number || "").toLowerCase().includes(q) ||
      (l.part_code || "").toLowerCase().includes(q) ||
      (l.order_code || "").toLowerCase().includes(q) ||
      (l.client_name || "").toLowerCase().includes(q) ||
      (l.operator_code || "").toLowerCase().includes(q)
    );
  }, [labels, search]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(l => l.id)));
    }
  };

  const reprintOne = (label) => {
    openBoxLabels([label], { autoPrint: true });
  };

  const reprintSelected = () => {
    const chosen = filtered.filter(l => selected.has(l.id));
    if (chosen.length === 0) { toast.error("Selecione pelo menos uma etiqueta"); return; }
    openBoxLabels(chosen, { autoPrint: true });
    toast.success(`A imprimir ${chosen.length} etiqueta(s)`);
  };

  const reprintAll = () => {
    if (filtered.length === 0) { toast.error("Sem etiquetas para imprimir"); return; }
    openBoxLabels(filtered, { autoPrint: true });
    toast.success(`A imprimir ${filtered.length} etiqueta(s)`);
  };

  const handleDelete = async (label) => {
    if (!window.confirm(`Apagar etiqueta ${label.lot_number}?`)) return;
    try {
      await axios.delete(`${API}/box-labels/${label.id}`);
      toast.success("Etiqueta apagada");
      fetchLabels();
    } catch (e) { showError(e, "Erro a apagar"); }
  };

  const totalQty = filtered.reduce((s, l) => s + (l.quantity || 0), 0);
  const totalWeight = filtered.reduce((s, l) => s + (l.weight_kg || 0), 0);

  return (
    <div className="space-y-5 p-6">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <QrCode className="h-7 w-7 text-orange-500" />
            Etiquetas de Caixa
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Consulta e reimpressão de etiquetas com QR Code (rastreabilidade)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchLabels} data-testid="bl-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <Button onClick={reprintSelected} disabled={selected.size === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="bl-print-selected">
            <Printer className="h-4 w-4 mr-2" />
            Imprimir Selecionadas ({selected.size})
          </Button>
          <Button onClick={reprintAll} disabled={filtered.length === 0} className="bg-orange-500 hover:bg-orange-600" data-testid="bl-print-all">
            <Printer className="h-4 w-4 mr-2" />
            Imprimir Todas ({filtered.length})
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center"><div className="text-xs text-muted-foreground">Etiquetas</div><div className="text-2xl font-bold">{filtered.length}</div></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><div className="text-xs text-muted-foreground">Total Peças</div><div className="text-2xl font-bold text-orange-600">{totalQty.toLocaleString("pt-PT")}</div></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><div className="text-xs text-muted-foreground">Total Peso</div><div className="text-2xl font-bold text-blue-700">{totalWeight.toFixed(1)} kg</div></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><div className="text-xs text-muted-foreground">Selecionadas</div><div className="text-2xl font-bold text-emerald-600">{selected.size}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <select
                className="h-9 px-3 rounded-md border bg-background text-sm"
                value={selectedOrder}
                onChange={(e) => { setSelectedOrder(e.target.value); setSelected(new Set()); }}
                data-testid="bl-order-filter"
              >
                <option value="all">📋 Todas as Ordens</option>
                {orders.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.code} — {o.status}
                  </option>
                ))}
              </select>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar lote, peça, ordem..."
                  className="pl-8 h-9 w-72"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="bl-search"
                />
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onCheckedChange={toggleAll}
                      data-testid="bl-toggle-all"
                    />
                  </TableHead>
                  <TableHead>Lote</TableHead>
                  <TableHead>Ordem</TableHead>
                  <TableHead>Peça</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-center">Caixa #</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead className="text-right">Peso</TableHead>
                  <TableHead>Operador</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(label => (
                  <TableRow key={label.id} className={selected.has(label.id) ? "bg-blue-50" : ""}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(label.id)}
                        onCheckedChange={() => toggle(label.id)}
                        data-testid={`bl-check-${label.lot_number}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs font-bold">{label.lot_number}</TableCell>
                    <TableCell>{label.order_code}</TableCell>
                    <TableCell>{label.part_code} — {label.part_description}</TableCell>
                    <TableCell>{label.client_name || "-"}</TableCell>
                    <TableCell className="text-center font-bold">#{label.box_number}</TableCell>
                    <TableCell className="text-right font-mono">{(label.quantity || 0).toLocaleString("pt-PT")}</TableCell>
                    <TableCell className="text-right font-mono">{(label.weight_kg || 0).toFixed(2)} kg</TableCell>
                    <TableCell>{label.operator_code} <span className="text-xs text-muted-foreground">({SHIFT_LABEL[label.shift] || label.shift})</span></TableCell>
                    <TableCell className="text-xs">{label.created_at ? new Date(label.created_at).toLocaleString("pt-PT") : "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => reprintOne(label)} title="Reimprimir" data-testid={`bl-reprint-${label.lot_number}`}>
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(label)} className="text-destructive" title="Apagar" data-testid={`bl-delete-${label.lot_number}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-10">
                      {search ? "Nenhuma etiqueta encontrada com esse critério" : "Sem etiquetas criadas. Use o Painel do Operador → Etiq. Caixa."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
